import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Slider } from "#/components/ui/slider";
import {
	detectPreset,
	type FilterState,
	getPresetValues,
	STABILITY_PRESETS,
	type StabilityPreset,
} from "#/lib/listingFilters";
import { cn } from "#/lib/utils";

const SPEED_RANGE = [1, 14] as const;
const GLIDE_RANGE = [1, 7] as const;
const TURN_RANGE = [-5, 1] as const;
const FADE_RANGE = [0, 5] as const;

function FlightSliderRow({
	label,
	min,
	max,
	filterMin,
	filterMax,
	onChange,
	formatVal,
}: {
	label: string;
	min: number;
	max: number;
	filterMin: number | "";
	filterMax: number | "";
	onChange: (val: { min: number | ""; max: number | "" }) => void;
	formatVal?: (n: number) => string;
}) {
	const lo = filterMin !== "" ? filterMin : min;
	const hi = filterMax !== "" ? filterMax : max;
	const [display, setDisplay] = useState([lo, hi]);
	const fmt = formatVal ?? String;

	useEffect(() => {
		setDisplay([lo, hi]);
	}, [lo, hi]);

	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex items-center justify-between">
				<span className="text-xs font-medium text-foreground">{label}</span>
				<span className="text-xs tabular-nums text-muted-foreground">
					{fmt(display[0])} – {fmt(display[1])}
				</span>
			</div>
			<Slider
				min={min}
				max={max}
				step={1}
				value={display as [number, number]}
				onValueChange={setDisplay}
				onValueCommit={(vals) =>
					onChange({
						min: vals[0] === min ? "" : vals[0],
						max: vals[1] === max ? "" : vals[1],
					})
				}
				className="w-full"
			/>
		</div>
	);
}

export function FlightFilter({
	filters,
	onChange,
}: {
	filters: FilterState;
	onChange: (next: Partial<FilterState>) => void;
}) {
	const { t } = useTranslation("browse");
	const activePreset = detectPreset(filters);

	function applyPreset(preset: StabilityPreset) {
		if (activePreset === preset) {
			onChange({ turnMin: "", turnMax: "", fadeMin: "", fadeMax: "" });
		} else {
			onChange(getPresetValues(preset));
		}
	}

	return (
		<div className="flex flex-col gap-4 p-1 w-[240px]">
			<div className="flex flex-col gap-3">
				<FlightSliderRow
					label={t("flightNumbers.speed")}
					min={SPEED_RANGE[0]}
					max={SPEED_RANGE[1]}
					filterMin={filters.speedMin}
					filterMax={filters.speedMax}
					onChange={({ min, max }) =>
						onChange({ speedMin: min, speedMax: max })
					}
				/>
				<FlightSliderRow
					label={t("flightNumbers.glide")}
					min={GLIDE_RANGE[0]}
					max={GLIDE_RANGE[1]}
					filterMin={filters.glideMin}
					filterMax={filters.glideMax}
					onChange={({ min, max }) =>
						onChange({ glideMin: min, glideMax: max })
					}
				/>
				<FlightSliderRow
					label={t("flightNumbers.turn")}
					min={TURN_RANGE[0]}
					max={TURN_RANGE[1]}
					filterMin={filters.turnMin}
					filterMax={filters.turnMax}
					onChange={({ min, max }) => onChange({ turnMin: min, turnMax: max })}
					formatVal={(n) => (n > 0 ? `+${n}` : String(n))}
				/>
				<FlightSliderRow
					label={t("flightNumbers.fade")}
					min={FADE_RANGE[0]}
					max={FADE_RANGE[1]}
					filterMin={filters.fadeMin}
					filterMax={filters.fadeMax}
					onChange={({ min, max }) => onChange({ fadeMin: min, fadeMax: max })}
				/>
			</div>
			<div className="flex flex-col gap-1.5 border-t border-border pt-3">
				<span className="text-xs font-medium text-muted-foreground">
					{t("flightFilter.stability")}
				</span>
				<div className="flex gap-1.5">
					{STABILITY_PRESETS.map(({ value }) => (
						<button
							key={value}
							type="button"
							onClick={() => applyPreset(value)}
							className={cn(
								"flex-1 rounded-md border px-2 py-1 text-xs font-medium transition-all",
								activePreset === value
									? "border-primary bg-primary/10 text-primary-accessible"
									: "border-border text-foreground hover:border-foreground/30 hover:bg-muted",
							)}
						>
							{t(`stabilityPresets.${value}`)}
						</button>
					))}
				</div>
			</div>
		</div>
	);
}
